import { extendObservable, runInAction } from 'mobx';

import { STOCK_LEVELS } from '../models/Stock';
import { Logger } from '../helpers/Logger';
import { NumberHelper } from '../helpers/NumberHelper';
import { ProductsController } from '../networking/controllers/ProductsController';
import { SuggestionsController } from '../networking/controllers/SuggestionsController';
import { SUGGESTION_TYPES } from '../models/Suggestion';
import { ConfigParametersStore } from './ConfigParametersStore';

const SORT_OPTIONS = {
  RELEVANCE: 'relevante',
  PRICE_ASC: 'priceAsc',
  PRICE_DESC: 'priceDesc',
};

const FILTER_TYPES = {
  CATEGORY: 'category',
  PRICE: 'price',
  STOCK: 'stock',
};

const CHECK_TYPES = {
  SUB_MENU: 'submenu',
  PRODUCT_TYPE: 'productType',
};

const SUGGESTION_INTERVAL = 1000;

const MAX_PRICE_FILTER_DIGIT_ADJUSTMENT = 3;

class SearchStoreClass {
  constructor() {
    extendObservable(this, {
      error: false,
      filteredProducts: null,
      filters: null,
      query: null,
      queryFilter: null,
      selectedSortOrder: SORT_OPTIONS.RELEVANCE,
      suggestions: null,
      isFetchingSuggestionsData: false,
      get productSuggestions() {
        if (!this.suggestions) {
          return [];
        }
        return this.suggestions.filter(suggestion => (
          suggestion.type === SUGGESTION_TYPES.PRODUCT ||
          suggestion.type === SUGGESTION_TYPES.SPECIFIC_PRODUCT
        ));
      },
      get categorySuggestions() {
        if (!this.suggestions) {
          return [];
        }
        return this.suggestions.filter(suggestion => (
          suggestion.type === SUGGESTION_TYPES.SUB_MENU ||
          suggestion.type === SUGGESTION_TYPES.PRODUCT_TYPE
        ));
      },
      get filtersValid() {
        return this.filters && (
          !!this.filters[FILTER_TYPES.STOCK] ||
          (
            !!this.filters[FILTER_TYPES.PRICE] &&
            !!this.filters[FILTER_TYPES.PRICE].length
          ) ||
          !!this.filters[FILTER_TYPES.CATEGORY]
        );
      },
      get selectedFiltersCount() {
        let selected = 0;
        if (this.filters[FILTER_TYPES.PRICE]) {
          selected += this.filters[FILTER_TYPES.PRICE].filter(filter => filter.enabled).length;
        }
        if (this.filters[FILTER_TYPES.STOCK] &&
          this.filters[FILTER_TYPES.STOCK].enabled) {
          selected += 1;
        }
        return selected;
      },
    });
    this.filterTypes = FILTER_TYPES;
    this.checkTypes = CHECK_TYPES;
    this.products = null;
    this.sortOptions = SORT_OPTIONS;
    this.suggestionQuery = null;
    this.suggestionTimeoutSet = false;
  }

  _setCountForPriceFilters() {
    runInAction(() => {
      this.filters[FILTER_TYPES.PRICE].forEach((filter) => {
        this.filteredProducts.forEach((prod) => {
          const { financing: { price } } = prod;
          if (price >= filter.min && price <= filter.max) {
            // eslint-disable-next-line no-param-reassign
            filter.count += 1;
          }
        });
      });
    });
  }

  _generateStockFilters() {
    // Count products with stock
    let productsWithStock = 0;
    this.filteredProducts.forEach((product) => {
      if (product.stockLevel !== STOCK_LEVELS.none) {
        productsWithStock += 1;
      }
    });
    // Send filter
    return {
      count: productsWithStock,
      enabled: false,
    };
  }

  _generatePriceFilters() {
    // Find biggest and lowest price
    const priceLimits = this.filteredProducts
      .filter(prod => !!prod.financing.price)
      .reduce((acc, product) => (
        {
          max: product.financing.price > acc.max ? product.financing.price : acc.max,
          min: product.financing.price < acc.min ? product.financing.price : acc.min,
        }
      ), {
        max: 0,
        min: Number.MAX_SAFE_INTEGER,
      });
    // First, expand the limits to reasonable values.
    // For example: a minimum of 4150 is replaced with 4000,
    // a maximum of 25560 is replaced with 26000.
    let adjustedMax = 0;
    let adjustedMin = 0;
    if (priceLimits.max > 0) {
      adjustedMax = NumberHelper.roundIntegerUp(priceLimits.max, MAX_PRICE_FILTER_DIGIT_ADJUSTMENT);
    }
    if (priceLimits.min > 0) {
      adjustedMin = NumberHelper.roundIntegerDown(
        priceLimits.min,
        MAX_PRICE_FILTER_DIGIT_ADJUSTMENT,
      );
    }
    // Then, create the different slots.
    // First, calculate how many slots will be needed
    // slots = ceil(ln(floor(max / min)))
    // Then, split the interval in that many logarithmic steps.
    const slotAmount = Math.ceil(Math.log(Math.floor(adjustedMax / adjustedMin)));
    const thresholds = NumberHelper.logarithmicIntervalsFor(adjustedMax, adjustedMin, slotAmount);
    // Round up all interval thresholds except the lower one
    const roundedThresholds = thresholds.map((num, index) => {
      if (index > 0) {
        return (
          NumberHelper.roundIntegerUp(num, MAX_PRICE_FILTER_DIGIT_ADJUSTMENT)
        );
      }
      return num;
    });
    // Clean zero-length intervals
    const filteredThresholds = roundedThresholds.filter((num, index, array) => {
      if (index > 0 && num === array[index - 1]) {
        return false;
      }
      return true;
    });
    // Create Filters
    const filters = [];
    filteredThresholds.forEach((num, index, array) => {
      if (index < array.length - 1) {
        filters.push({
          count: 0, // Temporarily set count to zero
          enabled: false,
          max: array[index + 1],
          min: num,
        });
      }
    });
    // Sort the filters from biggest prices to lowest
    return filters.sort((a, b) => b.max - a.max);
  }

  _genereateCategoryFilter() {
    const categories = [];
    // For each product on the search will collect their categories and
    // sub menues and get their count of ocurrences to show on filters
    this.filteredProducts.forEach((element) => {
      const subMenuElement = categories.find(category => (
        element.subMenu.id === category.id
      ));
      // There are 3 different options, the category haven't been found, the category
      // is in the categories array but the product type isn't inside it, and the case
      // were both are already present.
      if (!subMenuElement) {
        const currentCategory = {
          id: element.subMenu.id,
          name: element.subMenu.name,
          count: 1,
          productTypes: [{
            id: element.productType.id,
            name: element.productType.name,
            count: 1,
            enabled: false,
          }],
          enabled: false,
        };
        categories.push(currentCategory);
      } else if (!subMenuElement.productTypes.find(categorySubMenu => (
        element.productType.id === categorySubMenu.id
      ))) {
        const currentSubMenu = {
          id: element.productType.id,
          name: element.productType.name,
          count: 1,
          enabled: false,
        };
        subMenuElement.count += 1;
        subMenuElement.productTypes.push(currentSubMenu);
      } else {
        subMenuElement.count += 1;
        const currentSubMenu = subMenuElement.productTypes.find(categorySubMenu => (
          element.productType.id === categorySubMenu.id
        ));
        currentSubMenu.count += 1;
      }
    });
    categories.sort((a, b) => {
      if (a.count < b.count) {
        return 1;
      }
      return -1;
    });
    return categories;
  }

  /*
    Filters have at least two properties:
    - count: amount of products that satisfy this filter
    - enabled: boolean that states if the filter is active.
  */
  _generateFilters() {
    return {
      [FILTER_TYPES.CATEGORY]: this._genereateCategoryFilter(),
      [FILTER_TYPES.PRICE]: this._generatePriceFilters(),
      [FILTER_TYPES.STOCK]: this._generateStockFilters(),
    };
  }

  _applyStockFilters() {
    const filter = this.filters[FILTER_TYPES.STOCK];
    if (filter && filter.enabled) {
      const newProducts = this.filteredProducts.filter(prod => (
        prod.stockLevel !== STOCK_LEVELS.none
      ));
      runInAction(() => {
        this.filteredProducts = newProducts;
      });
    }
  }

  _applyPriceFilters() {
    const priceFilters = this.filters[FILTER_TYPES.PRICE];
    // Get enabled filters
    const enabledFilters = priceFilters.filter(f => f.enabled);
    // If no filters are enabled, return
    if (!enabledFilters.length) {
      return;
    }
    // Filter the current products
    const newProducts = this.filteredProducts.filter(prod => (
      enabledFilters.reduce((acc, fil) => (
        acc || (prod.financing.price >= fil.min && prod.financing.price <= fil.max)
      ), false)
    ));
    runInAction(() => {
      this.filteredProducts = newProducts;
    });
  }

  _applyCategoryFilters() {
    const categoryFilter = this.filters[FILTER_TYPES.CATEGORY];
    const enabledFilters = [];
    categoryFilter.forEach((filter) => {
      if (filter.enabled) {
        enabledFilters.push({
          id: filter.id,
          type: CHECK_TYPES.SUB_MENU,
        });
      } else {
        filter.productTypes.forEach((productType) => {
          if (productType.enabled) {
            enabledFilters.push({
              id: productType.id,
              type: CHECK_TYPES.PRODUCT_TYPE,
            });
          }
        });
      }
    });
    // If no filters are enabled, return
    if (!enabledFilters.length) {
      return;
    }
    // Filter current products
    const newProducts = this.filteredProducts.filter(prod =>
      (enabledFilters.find((filteredElement) => {
        if ((filteredElement.id === prod.subMenu.id) &&
          (filteredElement.type === CHECK_TYPES.SUB_MENU)) {
          return prod;
        } else if ((filteredElement.id === prod.productType.id) &&
          (filteredElement.type === CHECK_TYPES.PRODUCT_TYPE)) {
          return prod;
        }
        return null;
      })));
    runInAction(() => {
      this.filteredProducts = newProducts;
    });
  }

  _applyAllFilters() {
    runInAction(() => {
      this.filteredProducts = [...this.products];
    });
    this._applyPriceFilters();
    this._applyStockFilters();
    this._applyCategoryFilters();
  }

  clearFilters() {
    runInAction(() => {
      if (this.filters[FILTER_TYPES.PRICE]) {
        this.filters[FILTER_TYPES.PRICE].forEach((filter) => {
          const f = filter;
          f.enabled = false;
        });
      }
      if (this.filters[FILTER_TYPES.STOCK]) {
        this.filters[FILTER_TYPES.STOCK].enabled = false;
      }
      this.filteredProducts = [...this.products];
    });
    this._applyPriceFilters();
    this._applyStockFilters();
  }

  // This function is used to get the category of selected check without matter if it is a
  // category or a sub menu.
  _getSubMenuFiltered(id, checkType) {
    const subMenuSelected = this.filters[FILTER_TYPES.CATEGORY].find((filter) => {
      if (filter.id === id && checkType === CHECK_TYPES.SUB_MENU) {
        return filter;
      }
      if (checkType === CHECK_TYPES.PRODUCT_TYPE) {
        return (filter.productTypes.find(productType => (productType.id === id)));
      }
      return null;
    });
    return subMenuSelected;
  }

  toggleFilter(filterType, value, checkType) {
    if (filterType === FILTER_TYPES.PRICE) {
      // Find the filter
      const filter = this.filters[FILTER_TYPES.PRICE].find(f => f.min === value);
      if (!filter) {
        return;
      }
      // Toggle it
      runInAction(() => {
        filter.enabled = !filter.enabled;
      });
    } else if (filterType === FILTER_TYPES.STOCK) {
      if (!this.filters[FILTER_TYPES.STOCK]) {
        return;
      }
      runInAction(() => {
        this.filters[FILTER_TYPES.STOCK].enabled = !this.filters[FILTER_TYPES.STOCK].enabled;
      });
    } else if (filterType === FILTER_TYPES.CATEGORY) {
      const subMenuSelected = this._getSubMenuFiltered(value, checkType);
      if (checkType === CHECK_TYPES.SUB_MENU) {
        // In this case the checkbox toggled is a sub menu.
        runInAction(() => {
          subMenuSelected.enabled = !subMenuSelected.enabled;
        });
        subMenuSelected.productTypes.forEach((element) => {
          runInAction(() => {
            // eslint-disable-next-line no-param-reassign
            element.enabled = subMenuSelected.enabled;
          });
        });
      } else if (subMenuSelected.enabled) {
        // In this case the checkbox toggled is a product type and it sub menu is checked.
        const currentProductType = subMenuSelected.productTypes.find(productType =>
          (productType.id === value));
        runInAction(() => {
          subMenuSelected.enabled = !subMenuSelected.enabled;
          currentProductType.enabled = !currentProductType.enabled;
        });
      } else {
        // In this case the checkbox toggled is a product type and it sub menu is unchecked.
        const currentProductType = subMenuSelected.productTypes.find(productType =>
          (productType.id === value));

        runInAction(() => {
          currentProductType.enabled = !currentProductType.enabled;
        });
        const allProductTypesSelected = (subMenuSelected.productTypes.length > 1)
          ? subMenuSelected.productTypes.reduce((acc, productType) => (
            acc && productType.enabled
          ), true)
          : subMenuSelected.productTypes[0].enabled;
        if (allProductTypesSelected) {
          runInAction(() => {
            subMenuSelected.enabled = true;
          });
        }
      }
    }
    this._applyAllFilters();
    this._applySort();
  }

  _applySort() {
    if (this.selectedSortOrder === SORT_OPTIONS.RELEVANCE) {
      runInAction(() => {
        this.filteredProducts = [...this.filteredProducts].sort((a, b) => (
          a.relevanceIndex - b.relevanceIndex
        ));
      });
    } else if (this.selectedSortOrder === SORT_OPTIONS.PRICE_ASC) {
      runInAction(() => {
        this.filteredProducts = [...this.filteredProducts].sort((a, b) => {
          if (a.financing.price === null) {
            return 1;
          } else if (b.financing.price === null) {
            return -1;
          }
          return a.financing.price - b.financing.price;
        });
      });
    } else if (this.selectedSortOrder === SORT_OPTIONS.PRICE_DESC) {
      runInAction(() => {
        this.filteredProducts = [...this.filteredProducts].sort((a, b) => {
          if (a.financing.price === null) {
            return 1;
          } else if (b.financing.price === null) {
            return -1;
          }
          return b.financing.price - a.financing.price;
        });
      });
    }
  }

  sort(option) {
    if (Object.values(SORT_OPTIONS).includes(option)) {
      runInAction(() => {
        this.selectedSortOrder = option;
      });
      this._applySort();
    }
  }

  async search(query, queryFilter, suggestionType, suggestionId) {
    if (this.query === query && (!queryFilter || this.queryFilter === queryFilter)) {
      return;
    }
    runInAction(() => {
      this.query = query;
      this.queryFilter = queryFilter;
      this.filters = null;
      this.filteredProducts = null;
      this.error = false;
    });
    try {
      let products = null;
      if (suggestionType === SUGGESTION_TYPES.SUB_MENU ||
        suggestionType === SUGGESTION_TYPES.PRODUCT_TYPE) {
        let subMenuId = null;
        if (suggestionType === SUGGESTION_TYPES.SUB_MENU) {
          subMenuId = suggestionId;
        }
        let productTypeId = null;
        if (suggestionType === SUGGESTION_TYPES.PRODUCT_TYPE) {
          productTypeId = suggestionId;
        }
        products = await ProductsController.findByCategory(
          subMenuId,
          productTypeId,
          queryFilter,
        );
      } else {
        products = await ProductsController.search(query, queryFilter);
      }
      this.products = products.map((product) => {
        const productPrice = ConfigParametersStore.isValueInFeatureFlagData('product-price', product.id);
        if (!product.shouldShowPrice() && productPrice) {
          // eslint-disable-next-line no-param-reassign
          product.financing = productPrice;
        }
        return product;
      });
      // Here we artificially add a relevance index. This helps sort by relevance.
      products.forEach((prod, index) => {
        Object.assign(prod, { relevanceIndex: index });
      });
      runInAction(() => {
        this.filteredProducts = [...this.products];
        this.filters = this._generateFilters();
      });
      this._setCountForPriceFilters();
      this._applySort();
    } catch (error) {
      runInAction(() => {
        this.error = true;
      });
      Logger.warn(error);
    }
  }

  suggest(query, queryFilter) {
    this.suggestionQuery = query;
    if (!query) {
      runInAction(() => {
        this.suggestions = null;
        this.isFetchingSuggestionsData = false;
      });
    }
    if (this.suggestionTimeoutSet || !query) {
      return;
    }
    this.suggestionTimeoutSet = true;
    window.setTimeout(async () => {
      runInAction(() => {
        this.suggestionTimeoutSet = false;
        this.isFetchingSuggestionsData = false;
      });
      if (!this.suggestionQuery) {
        runInAction(() => {
          this.suggestions = null;
        });
        return;
      }
      try {
        runInAction(() => {
          this.isFetchingSuggestionsData = true;
        });
        const data = await SuggestionsController.suggest(this.suggestionQuery, queryFilter);
        runInAction(() => {
          this.suggestions = data;
          this.isFetchingSuggestionsData = false;
        });
      } catch (error) {
        Logger.warn(error);
      }
    }, SUGGESTION_INTERVAL);
  }
}

const SearchStore = new SearchStoreClass();

export { SearchStore };



// WEBPACK FOOTER //
// ./src/stores/SearchStore.js